/* eslint-disable react/jsx-key */
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { TableScrollButtons } from './TableScrollButtons';
import { useIsScrollable } from '../../../../../../../common/react/hooks/use-is-scrollable';

const SCROLL_DEBOUNCE_MS = 100;

/**
 * @param root0
 * @param root0.children
 * @param root0.getTableProps
 * @param root0.nextPageButton
 * @param root0.modifier
 * @param root0.cssClass
 * @param root0.stickyHeader
 * @param root0.stickyPageNav
 */
export function StatsTable({
    children,
    getTableProps,
    nextPageButton,
    modifier,
    cssClass,
    stickyHeader,
    stickyPageNav
}) {
    const scrollWrapperRef = useRef();
    const scrollWrapperRef2 = useRef();

    const [scrollPosition, setScrollPosition] = useState(0);
    const [isHidden, setIsHidden] = useState(true);
    const [buttonClicked, setButtonClicked] = useState(false);

    const isTableScrollable = useIsScrollable(scrollWrapperRef);

    // Debounce needs to be set as a function to work correctly and then be called
    const debounceScrollPosition = _.debounce((value) => {
        setScrollPosition(value);
    }, SCROLL_DEBOUNCE_MS);

    const getHeaderOffset = (element) => {
        const rect = element?.getBoundingClientRect();
        return rect?.height;
    };

    const getOffset = (element) => {
        const pageHeader = getHeaderOffset(stickyPageNav);
        const rect = element?.getBoundingClientRect(),
            scrollTop =
                window.pageYOffset || document.documentElement.scrollTop;
        return rect?.top + scrollTop - pageHeader;
    };

    const listenToScroll = () => {
        let heightToHideFrom = getOffset(scrollWrapperRef.current);
        const winScroll =
            document.body.scrollTop || document.documentElement.scrollTop;
        const tableElement = getHeaderOffset(scrollWrapperRef.current);
        const tableEnd = heightToHideFrom + tableElement;

        if (winScroll > tableEnd || winScroll < heightToHideFrom) {
            setIsHidden(true);
        } else {
            setIsHidden(false);
            handleScrollSecond();
        }
    };

    const handleScrollFirst = () => {
        // Trigger debounce and update scrollPosition
        debounceScrollPosition(scrollWrapperRef2.current.scrollLeft);

        // Only scroll both at the same time if manual scroll triggered NOT button click
        // otherwise this only allows a scroll of 2px which causes a sticky bug
        if (!buttonClicked) {
            scrollWrapperRef.current.scrollLeft =
                scrollWrapperRef2.current.scrollLeft;
        }
    };

    const handleScrollSecond = () => {
        // Trigger debounce and update scrollPosition
        debounceScrollPosition(scrollWrapperRef.current.scrollLeft);

        // Only scroll both at the same time if manual scroll triggered NOT button click
        // otherwise this only allows a scroll of 2px which causes a sticky bug
        if (scrollWrapperRef2?.current && !buttonClicked) {
            scrollWrapperRef2.current.scrollLeft =
                scrollWrapperRef.current.scrollLeft;
        }
    };

    // ===== Effects ======================================================== //

    useEffect(() => {
        window.addEventListener('scroll', listenToScroll);
        return () => window.removeEventListener('scroll', listenToScroll);
    }, []);

    return (
        <>
            {isHidden ? (
                ''
            ) : (
                <div
                    className={classNames(
                        'stats-table ' + (cssClass ?? ''),
                        {
                            'stats-table--is-scrollable': isTableScrollable,
                            [`stats-table--${modifier}`]: !!modifier
                        },
                        'stats-table__dup'
                    )}
                >
                    <div
                        ref={scrollWrapperRef2}
                        className="stats-table__wrapper stats-table__dup-header"
                        onScroll={handleScrollFirst}
                    >
                        {isTableScrollable && (
                            <TableScrollButtons
                                scrollWrapperRef={scrollWrapperRef2}
                                stickyHeader={scrollWrapperRef}
                                scrollPosition={scrollPosition}
                                setButtonClicked={setButtonClicked}
                            />
                        )}
                        <table className="stats-table__table">
                            {stickyHeader}
                        </table>
                    </div>
                </div>
            )}
            <div
                className={classNames('stats-table ' + (cssClass ?? ''), {
                    'stats-table--is-scrollable': isTableScrollable,
                    [`stats-table--${modifier}`]: !!modifier
                })}
            >
                <div
                    ref={scrollWrapperRef}
                    className="stats-table__wrapper"
                    // @ts-ignore
                    onScroll={handleScrollSecond}
                >
                    {isTableScrollable && (
                        <TableScrollButtons
                            scrollWrapperRef={scrollWrapperRef}
                            scrollPosition={scrollPosition}
                            setButtonClicked={setButtonClicked}
                        />
                    )}

                    <table {...getTableProps()} className="stats-table__table">
                        {children}
                    </table>
                </div>
                {nextPageButton}
            </div>
        </>
    );
}

StatsTable.propTypes = {
    children: PropTypes.any.isRequired,
    getTableProps: PropTypes.func.isRequired,
    nextPageButton: PropTypes.any,
    modifier: PropTypes.string,
    cssClass: PropTypes.string,
    stickyHeader: PropTypes.element,
    stickyPageNav: PropTypes.any
};
